import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  Sparkles, ArrowRight, DollarSign, AlertTriangle, TrendingUp, CheckCircle2,
  Target, Loader2, Database, Flame, Cpu, Coffee, ShoppingCart, ArrowUpRight,
  TrendingDown, Minus,
} from "lucide-react";
import { VideoExplainerOverlay } from "@/components/VideoExplainer";
import ReferrerSessionsDrawer from "@/components/ReferrerSessionsDrawer";
import LiveFeed from "@/components/LiveFeed";

const COMMODITY_INDUSTRIES = new Set([
  "mining", "shipping", "energy", "resources", "minerals", "metals",
  "iron ore", "coal", "gold", "copper", "maritime", "logistics", "oil & gas",
]);

const POS_INDUSTRIES = new Set([
  "retail", "hospitality", "consumer goods", "consumer_goods",
  "food & beverage", "f&b", "ecommerce", "e-commerce", "fmcg", "restaurants",
]);

const ICON_BY_TYPE = {
  ANOMALY: AlertTriangle,
  OPPORTUNITY: DollarSign,
  PREDICTION: TrendingUp,
  TREND: Target,
};

const COLOR_BY_IMPACT = {
  HIGH: { bg: "bg-[#FFB000]/5", border: "border-[#FFB000]/40", text: "text-[#FFB000]" },
  MEDIUM: { bg: "bg-zinc-900/40", border: "border-zinc-800", text: "text-white" },
  LOW: { bg: "bg-zinc-950", border: "border-zinc-900", text: "text-zinc-400" },
};

const ACTION_LABEL = {
  ANOMALY: "Investigate",
  OPPORTUNITY: "Take action",
  PREDICTION: "Plan for this",
  TREND: "Review details",
};

function formatMoney(n, currency = "USD") {
  if (!n || n < 0.01) return null;
  if (n >= 1_000_000) return `${currency} ${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${currency} ${(n / 1_000).toFixed(0)}K`;
  return `${currency} ${n.toFixed(0)}`;
}


/**
 * Phase 34 — Today / Simple mode.
 * One screen. Three insight cards. A button each.
 * Target audience: a founder / operator who doesn't have time for 11 tabs.
 */
export default function Today() {
  const { user } = useAuth();
  const nav = useNavigate();
  const explainerUrl = process.env.REACT_APP_EXPLAINER_VIDEO_URL || "";
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [sourceCount, setSourceCount] = useState(null);
  const [streak, setStreak] = useState({ current_streak: 0, longest_streak: 0, acted_today: false });
  const [workspaceIndustry, setWorkspaceIndustry] = useState("");
  const [posSummary, setPosSummary] = useState(null);
  const [organicRefs, setOrganicRefs] = useState(null); // {window_days, top: [{referrer, views}], total_visitors}
  const [drillReferrer, setDrillReferrer] = useState(null); // active drill-through referrer
  const [industrialDismissed, setIndustrialDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem("dara:industrial-card-dismissed") === "1";
  });

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const [insResp, srcResp, stResp, wsResp] = await Promise.all([
          api.get("/insights"),
          api.get("/data-sources").catch(() => ({ data: [] })),
          api.get("/today/streak").catch(() => ({ data: { current_streak: 0, longest_streak: 0, acted_today: false } })),
          api.get("/workspace").catch(() => ({ data: {} })),
        ]);
        if (cancel) return;
        setInsights(insResp.data || []);
        const items = Array.isArray(srcResp.data) ? srcResp.data
                    : (srcResp.data?.items || []);
        setSourceCount(items.length);
        setStreak(stResp.data);
        setWorkspaceIndustry(wsResp.data?.industry || "");
        // POS summary — only fetch if workspace is POS-relevant
        const ind = (wsResp.data?.industry || "").toLowerCase().trim();
        if (POS_INDUSTRIES.has(ind)) {
          try {
            const [t, pb] = await Promise.all([
              api.get("/pos/today"),
              api.get("/pos/playbook").catch(() => ({ data: null })),
            ]);
            if (!cancel && (t.data?.txn_count || 0) > 0) {
              setPosSummary({ today: t.data, playbook: pb.data });
            }
          } catch { /* non-fatal */ }
        }
      } catch (e) {
        // degraded — keep UI usable
      }
      setLoading(false);
    })();
    return () => { cancel = true; };
  }, []);

  // Phase 25.13n — Super-admin organic-referrer widget. Pulls last-30d
  // genuine (non-internal, non-bot) referrers from /admin/visitors. Filters
  // out (direct)/(internal) so only organic acquisition channels surface.
  useEffect(() => {
    if (!user?.is_super_admin) return;
    let cancel = false;
    (async () => {
      try {
        const r = await api.get("/admin/visitors?days=30");
        if (cancel) return;
        const refs = (r.data?.top_referrers || [])
          .filter(x => x.referrer && x.referrer !== "(direct)" && x.referrer !== "(internal)")
          .slice(0, 5);
        setOrganicRefs({
          window_days: r.data?.window_days || 30,
          top: refs,
          total_visitors: r.data?.unique_visitors || 0,
          total_pageviews: r.data?.total_pageviews || 0,
        });
      } catch (e) {
        // non-fatal — just hide the widget
      }
    })();
    return () => { cancel = true; };
  }, [user?.is_super_admin]);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post("/insights/generate");
      setInsights(prev => [...(r.data || []), ...prev]);
      toast.success(`${r.data?.length || 0} fresh insights`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate insights");
    }
    setGenerating(false);
  };

  const actOnInsight = async (insight) => {
    // Optimistic UX — record + navigate; bump streak counter immediately if first action today
    try {
      const r = await api.post("/today/act", {
        insight_id: insight.id,
        label: ACTION_LABEL[insight.type] || "Explore",
      });
      if (r?.data) setStreak(r.data);
    } catch (e) {
      // non-blocking — still navigate
    }
    nav("/app/insights");
  };

  // Top 3 by impact + recency
  const top = [...insights]
    .sort((a, b) => {
      const rank = { HIGH: 3, MEDIUM: 2, LOW: 1 };
      const ra = (rank[a.impact] || 0) * 1_000_000_000 + (new Date(a.created_at).getTime() || 0);
      const rb = (rank[b.impact] || 0) * 1_000_000_000 + (new Date(b.created_at).getTime() || 0);
      return rb - ra;
    })
    .slice(0, 3);

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
  })();
  const firstName = user?.name?.split(" ")[0] || "there";

  // Empty states
  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-[#FFB000]" />
      </div>
    );
  }

  if (sourceCount === 0) {
    return (
      <div className="p-6 sm:p-10 max-w-2xl mx-auto" data-testid="today-empty-connect">
        <VideoExplainerOverlay
          videoUrl={explainerUrl}
          title="Welcome to DARA — see it in 90 seconds"
          description="Quick tour of the modules that matter most: connecting data, generating insights, hedging exposures, and exporting board-ready PDFs."
        />
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          // WELCOME TO DARA
        </div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter mt-2">
          Let&apos;s start with your data.
        </h1>
        <p className="text-zinc-400 mt-4 text-sm leading-relaxed">
          DARA turns your existing data into a single prioritised list of what to do next.
          Connect one source to begin — we&apos;ll do the rest.
        </p>
        <div className="mt-8 panel p-6">
          <div className="flex items-start gap-4">
            <Database className="w-5 h-5 text-[#FFB000] mt-1" />
            <div className="flex-1">
              <div className="font-bold text-white">Connect your first data source</div>
              <p className="text-zinc-500 text-sm mt-1">
                Xero, a CSV file, Slack — any of these work. Takes 60 seconds.
              </p>
              <button
                onClick={() => nav("/app/sources")}
                data-testid="today-connect-cta"
                className="mt-4 bg-[#FFB000] text-black font-bold px-5 py-2.5 text-sm hover:opacity-90 inline-flex items-center gap-2"
              >
                Connect data <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (top.length === 0) {
    return (
      <div className="p-6 sm:p-10 max-w-2xl mx-auto" data-testid="today-empty-generate">
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          // {greeting.toUpperCase()}
        </div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter mt-2">
          {greeting}, {firstName}.
        </h1>
        <p className="text-zinc-400 mt-4 text-sm leading-relaxed">
          You have {sourceCount} data source{sourceCount === 1 ? "" : "s"} connected.
          Ask DARA to surface the 3 most important things for you this week.
        </p>
        <button
          onClick={generate}
          disabled={generating}
          data-testid="today-first-generate-btn"
          className="mt-8 bg-[#FFB000] text-black font-bold px-5 py-2.5 text-sm hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2"
        >
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {generating ? "Thinking…" : "Show me what matters"}
        </button>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8 lg:p-12 max-w-4xl mx-auto" data-testid="today-page">
      <VideoExplainerOverlay
        videoUrl={explainerUrl}
        title="Welcome to DARA — see it in 90 seconds"
        description="Quick tour of the modules that matter most: connecting data, generating insights, hedging exposures, and exporting board-ready PDFs."
      />
      {/* Friendly header */}
      <div className="mb-8 sm:mb-10">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            // {greeting.toUpperCase()} · {new Date().toLocaleDateString("en", { weekday: "long", month: "long", day: "numeric" })}
          </div>
          {streak.current_streak > 0 && (
            <div
              data-testid="today-streak-chip"
              title={`Longest streak: ${streak.longest_streak} day${streak.longest_streak === 1 ? "" : "s"}`}
              className={`inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 border ${
                streak.acted_today
                  ? "border-[#FFB000]/50 text-[#FFB000] bg-[#FFB000]/5"
                  : "border-zinc-800 text-zinc-400"
              }`}
            >
              <Flame className={`w-3 h-3 ${streak.acted_today ? "animate-pulse" : ""}`} />
              <span>
                {streak.current_streak} DAY{streak.current_streak === 1 ? "" : "S"}
              </span>
            </div>
          )}
        </div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter mt-2">
          {greeting}, <span className="text-[#FFB000]">{firstName}</span>.
        </h1>
        <p className="text-zinc-400 text-base sm:text-lg mt-3 leading-relaxed">
          Here {top.length === 1 ? "is the one thing" : `are the ${top.length} things`} that matter this week.
        </p>
      </div>

      {/* Cards */}
      <div className="space-y-4" data-testid="today-cards">
        {top.map((insight, i) => {
          const Icon = ICON_BY_TYPE[insight.type] || Target;
          const palette = COLOR_BY_IMPACT[insight.impact] || COLOR_BY_IMPACT.MEDIUM;
          const money = formatMoney(insight.estimated_value, user?.currency_code || "USD");
          return (
            <div
              key={insight.id}
              data-testid={`today-card-${i}`}
              className={`panel border-2 p-5 sm:p-6 ${palette.border} ${palette.bg}`}
            >
              <div className="flex items-start gap-4">
                <div className={`p-3 border ${palette.border} ${palette.text}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="font-heading font-black text-lg sm:text-xl tracking-tight leading-tight flex-1 min-w-0">
                      {insight.title}
                    </div>
                    {money && (
                      <div className={`font-mono text-base sm:text-lg font-bold ${palette.text}`}>
                        {money}
                      </div>
                    )}
                  </div>
                  <p className="text-zinc-400 text-sm leading-relaxed mt-3">
                    {insight.description}
                  </p>
                  <div className="flex items-center justify-between mt-5 flex-wrap gap-3">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">
                      {insight.impact} IMPACT · {insight.confidence}% CONFIDENT
                    </div>
                    <button
                      onClick={() => actOnInsight(insight)}
                      data-testid={`today-action-${i}`}
                      className={`border px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-white/5 flex items-center gap-2 ${palette.border} ${palette.text}`}
                    >
                      {ACTION_LABEL[insight.type] || "Explore"}
                      <ArrowRight className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* POS Live summary card — visible only for SMB/retail/hospitality workspaces with POS data */}
      {posSummary && (
        <div
          data-testid="today-pos-card"
          className="mt-8 border border-[#00E676]/30 bg-gradient-to-br from-[#00E676]/[0.03] to-transparent p-5 sm:p-6 cursor-pointer hover:bg-[#00E676]/[0.05] transition-colors"
          onClick={() => nav("/app/pos")}
          role="button"
          tabIndex={0}
          onKeyDown={e => (e.key === "Enter" || e.key === " ") && nav("/app/pos")}
        >
          <div className="flex items-start gap-4">
            <div className="p-3 border border-[#00E676]/40 text-[#00E676] flex-shrink-0">
              <ShoppingCart className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#00E676]">
                  // POS LIVE · TODAY
                </span>
                <span className="font-mono text-[9px] uppercase tracking-widest text-[#00E676] border border-[#00E676]/40 bg-[#00E676]/5 px-1.5 py-px inline-flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#00E676] animate-pulse" /> LIVE
                </span>
              </div>
              <div className="flex items-baseline gap-3 flex-wrap mt-1">
                <h3 className="font-heading font-black text-2xl sm:text-3xl tracking-tighter text-white">
                  {posSummary.today.revenue_cents != null
                    ? `${user?.currency_symbol || "$"}${(posSummary.today.revenue_cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                    : `—`}
                </h3>
                <span className="font-mono text-[11px] text-zinc-500">
                  · {posSummary.today.txn_count} txns · avg {user?.currency_symbol || "$"}{((posSummary.today.avg_ticket_cents || 0) / 100).toFixed(2)}
                </span>
              </div>
              {posSummary.playbook?.summary && (
                <p className="text-sm text-zinc-300 mt-3 leading-relaxed line-clamp-2">
                  <Coffee className="w-3.5 h-3.5 text-[#FFB000] inline-block mr-1.5 -mt-0.5" />
                  {posSummary.playbook.summary.slice(0, 180)}{posSummary.playbook.summary.length > 180 ? "…" : ""}
                </p>
              )}
              {posSummary.playbook?.close_of_day?.projection_cents != null && (
                <div className="font-mono text-[10px] text-zinc-500 mt-3">
                  → Close-of-day projection:{" "}
                  <span className="text-[#FFB000]">
                    {user?.currency_symbol || "$"}{(posSummary.playbook.close_of_day.projection_cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                  {" "}({posSummary.playbook.close_of_day.delta_vs_baseline_pct >= 0 ? "+" : ""}{posSummary.playbook.close_of_day.delta_vs_baseline_pct}% vs same-DOW avg)
                </div>
              )}
              <div className="flex items-center gap-1.5 mt-4 text-[#00E676] font-mono text-[10px] uppercase tracking-widest">
                Open POS Live <ArrowRight className="w-3 h-3" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Refresh + sign-off */}
      <div className="mt-10 flex items-center justify-between flex-wrap gap-3 border-t border-zinc-900 pt-6">
        <div className="font-mono text-[11px] text-zinc-500">
          {insights.length} total insight{insights.length === 1 ? "" : "s"} · based on {sourceCount} source{sourceCount === 1 ? "" : "s"}
        </div>
        <button
          onClick={generate}
          disabled={generating}
          data-testid="today-regenerate-btn"
          className="border border-zinc-800 text-zinc-300 px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-zinc-900 inline-flex items-center gap-2 disabled:opacity-50"
        >
          {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
          {generating ? "Thinking…" : "Refresh insights"}
        </button>
      </div>

      {/* Industrial Adapter teaser — visible only for heavy-industry workspaces */}
      {!industrialDismissed && COMMODITY_INDUSTRIES.has((workspaceIndustry || "").toLowerCase().trim()) && (
        <div
          data-testid="today-industrial-card"
          className="mt-8 border border-[#FFB000]/30 bg-gradient-to-br from-[#FFB000]/[0.03] to-transparent p-5 sm:p-6 relative"
        >
          <button
            onClick={() => {
              setIndustrialDismissed(true);
              try { localStorage.setItem("dara:industrial-card-dismissed", "1"); } catch (e) { /* ignore */ }
            }}
            data-testid="today-industrial-dismiss"
            aria-label="Dismiss"
            className="absolute top-2 right-2 font-mono text-[10px] text-zinc-600 hover:text-zinc-300 px-2 py-1"
          >
            ×
          </button>
          <div className="flex items-start gap-4">
            <div className="p-3 border border-[#FFB000]/40 text-[#FFB000] flex-shrink-0">
              <Cpu className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FFB000]">
                  // INDUSTRIAL ADAPTER · Q3 2026
                </span>
                <span className="font-mono text-[9px] uppercase tracking-widest text-[#00E676] border border-[#00E676]/40 bg-[#00E676]/5 px-1.5 py-px">
                  PRIVATE BETA
                </span>
              </div>
              <h3 className="font-heading font-black text-lg sm:text-xl tracking-tight mt-1">
                Plug DARA into your factory floor.
              </h3>
              <p className="text-sm text-zinc-400 mt-2 leading-relaxed">
                Stream OPC UA, MODBUS, and SCADA telemetry into the same intelligence layer
                that drives your Hedge Ledger. Live tonnes-per-shift forecasting, predictive
                maintenance alerts, and real-time ESG disclosure pre-fill — no new sensors required.
              </p>
              <div className="flex items-center gap-3 mt-4 flex-wrap">
                <a
                  href="/industrial#waitlist"
                  data-testid="today-industrial-cta"
                  className="bg-[#FFB000] text-black font-bold font-mono text-[10px] uppercase tracking-widest px-3.5 py-2 hover:opacity-90 inline-flex items-center gap-1.5"
                >
                  JOIN WAITLIST <ArrowRight className="w-3 h-3" />
                </a>
                <a
                  href="/industrial"
                  className="border border-zinc-800 hover:border-zinc-600 font-mono text-[10px] uppercase tracking-widest px-3.5 py-2 text-zinc-300 hover:text-white"
                >
                  HOW IT WORKS
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Phase 25.13v — Near-live data ticker visible to every operator.
          Pings heartbeat every 30s, polls events every 10s, runs Xero/MYOB
          delta-sync every 5 min on the backend. */}
      <LiveFeed />

      {/* Phase 25.13n — Super-admin only: organic-referrer pulse (last 30d). */}
      {user?.is_super_admin && organicRefs && (
        <div
          data-testid="today-organic-refs"
          className="mt-8 border border-zinc-900 bg-[#040404] p-5 sm:p-6"
        >
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <div>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FFB000]">
                // ACQUISITION · TOP ORGANIC REFERRERS · LAST 30D
              </span>
              <div className="font-mono text-[10px] text-zinc-600 mt-1">
                {organicRefs.total_visitors} genuine visitor{organicRefs.total_visitors === 1 ? "" : "s"} · {organicRefs.total_pageviews} pageview{organicRefs.total_pageviews === 1 ? "" : "s"} · own-team filtered
              </div>
            </div>
            <button
              type="button"
              onClick={() => nav("/app/admin/visitors")}
              data-testid="today-organic-refs-open"
              className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-[#FFB000] flex items-center gap-1"
            >
              Open visitors <ArrowRight className="w-3 h-3" />
            </button>
          </div>

          {organicRefs.top.length === 0 ? (
            <div className="font-mono text-[11px] text-zinc-600 leading-relaxed py-4 text-center">
              No organic referrers yet in this window. Drop your <span className="text-[#FFB000]">daraos.ai</span> link in a LinkedIn post,
              a HackerNews comment, or a guest essay — and watch the channel light up here.
            </div>
          ) : (
            <ul className="divide-y divide-zinc-900">
              {organicRefs.top.map((r, i) => {
                const delta = r.wow_delta || 0;
                const isUp = delta > 0;
                const isDown = delta < 0;
                const trendIcon = isUp ? TrendingUp : isDown ? TrendingDown : Minus;
                const TrendIcon = trendIcon;
                const trendColor = isUp ? "text-[#00E676]" : isDown ? "text-[#FF3B30]" : "text-zinc-600";
                const trendLabel = isUp ? `+${delta}` : isDown ? `${delta}` : "flat";
                return (
                  <li key={r.referrer} data-testid={`organic-ref-${i}`}>
                    <button
                      type="button"
                      onClick={() => setDrillReferrer(r.referrer)}
                      data-testid={`organic-ref-${i}-drill`}
                      className="w-full text-left flex items-center justify-between py-2.5 gap-3 hover:bg-zinc-950/40 transition-colors px-1"
                      title={`Click to see every session from ${r.referrer}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-mono text-[9px] text-zinc-700 w-5">{String(i + 1).padStart(2, "0")}</span>
                        <ArrowUpRight className="w-3.5 h-3.5 text-zinc-600 flex-shrink-0" />
                        <span className="font-mono text-[12px] text-zinc-200 truncate group-hover:text-[#FFB000]">{r.referrer}</span>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className={`font-mono text-[10px] uppercase tracking-widest ${trendColor} flex items-center gap-1`}>
                          <TrendIcon className="w-3 h-3" /> {trendLabel}
                        </span>
                        <span className="font-mono text-[12px] text-[#FFB000] min-w-[70px] text-right">{r.views} views</span>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {/* Trust footer */}
      <div className="mt-6 flex items-center gap-2 font-mono text-[10px] text-zinc-700">
        <CheckCircle2 className="w-3 h-3 text-[#00E676]" />
        Grounded in your data · DARA Engine · No training on your inputs
      </div>

      <ReferrerSessionsDrawer
        referrer={drillReferrer}
        days={30}
        onClose={() => setDrillReferrer(null)}
      />
    </div>
  );
}
